import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useLocation } from "../contexts/LocationContext";
import { useLocation as useRouterLocation } from "react-router-dom";
import { useSettings } from "../contexts/SettingsContext";
import CreateNoteModal from "../components/CreateNoteModal";
import MapboxMap from "../components/map/MapboxMap";
import BlockingModal from "../components/BlockingModal";
import NoteModal from "../components/notes/NoteModal";
import Spinner from "../ui/Spinner";
import { PencilIcon } from "@heroicons/react/24/outline";
import { devLog, logError } from "../utils/logger";
import { computeRadiusBounds } from "../utils/createCircle";
import { convertDistance } from "../utils/units";
import OnboardingOverlay from "../components/onboarding/OnboardingOverlay";
import { useOnboarding } from "../hooks/useOnboarding";
import { useAuth } from "../contexts/AuthContext";
import { useVisibility } from "../contexts/VisibilityContext";

// New mock backend
import { useNearbyNotes } from "../hooks/useNearbyNotes";
import { api } from "../api";

export default function UserDashboard() {
  const [showCreateNoteModal, setShowCreateNoteModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  // Onboarding wizard
  const onboarding = useOnboarding();

  // Get user data for role-based range
  const { user, isAdmin } = useAuth();
  const { isVisible } = useVisibility();
  const userMaxRange = user?.maxRange || 50;
  const showSeeAllToggle = isAdmin;

  // "See All" toggle for extended-range users (admins, custom ranges)
  const [seeAllNotes, setSeeAllNotes] = useState(() => {
    if (!showSeeAllToggle) return false;
    const saved = sessionStorage.getItem("traces_see_all_notes");
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Get user's unit preference from settings
  const { settings } = useSettings();
  const unit = settings?.units || "miles";
  const unitSuffix = unit === "miles" ? "mi" : "km";

  // Radius control state with localStorage persistence (clamped to user's maxRange)
  const [radius, setRadius] = useState(() => {
    const saved = localStorage.getItem("traces_search_radius");
    if (!saved) return Math.min(15, userMaxRange);

    // Support legacy format (plain number) and new format ({ value, unit })
    let value, savedUnit;
    try {
      const parsed = JSON.parse(saved);
      if (typeof parsed === 'object' && parsed !== null) {
        value = parsed.value;
        savedUnit = parsed.unit;
      } else {
        value = parseFloat(saved);
      }
    } catch {
      value = parseFloat(saved);
    }

    if (isNaN(value)) return Math.min(15, userMaxRange);

    // Convert if the saved unit differs from the current unit
    if (savedUnit && savedUnit !== unit) {
      value = convertDistance(value, savedUnit, unit);
    }

    return Math.min(Math.round(value), userMaxRange);
  });

  // Tracks the radius value committed on slider release (triggers map fitBounds animation)
  const [committedRadius, setCommittedRadius] = useState(radius);

  const handleRadiusCommitted = useCallback((value) => {
    setCommittedRadius(value ?? radius);
  }, [radius]);

  // Track if user has explicitly changed the radius circle toggle this session
  const [hasUserToggledCircle, setHasUserToggledCircle] = useState(() => {
    return sessionStorage.getItem("traces_show_radius_circle_session") !== null;
  });

  const [showRadiusCircle, setShowRadiusCircle] = useState(() => {
    // 1. Check for session override first (temporary override for current session)
    const sessionOverride = sessionStorage.getItem(
      "traces_show_radius_circle_session",
    );
    if (sessionOverride !== null) {
      return JSON.parse(sessionOverride);
    }
    // 2. Fall back to settings default (persistent across sessions)
    return settings?.showRadiusCircle ?? true;
  });

  // Wrapper to track when user explicitly toggles the circle
  const handleRadiusCircleToggle = useCallback((value) => {
    setShowRadiusCircle(value);
    setHasUserToggledCircle(true);
  }, []);

  // Use location context for location data
  const {
    userLocation,
    locationError,
    isLoading: locationLoading,
    requestLocation,
  } = useLocation();

  // Compute effective radius: "See All" uses full maxRange, otherwise use slider
  const effectiveRadius = (showSeeAllToggle && seeAllNotes)
    ? userMaxRange
    : Math.round(committedRadius);

  // Fetch nearby notes based on user location with debounced radius
  const {
    notes,
    loading,
    fetchingNote,
    error,
    refetch,
    createNote,
    fetchNote,
    deleteNote,
  } = useNearbyNotes(userLocation, effectiveRadius, unit);

  devLog("Home.js render:", {
    userLocation,
    notes,
    loading,
    error,
    locationLoading,
    locationError,
  });

  // Persist radius and unit to localStorage
  useEffect(() => {
    localStorage.setItem("traces_search_radius", JSON.stringify({ value: radius, unit }));
  }, [radius, unit]);

  // Persist seeAllNotes to sessionStorage
  useEffect(() => {
    if (showSeeAllToggle) {
      sessionStorage.setItem("traces_see_all_notes", JSON.stringify(seeAllNotes));
    }
  }, [seeAllNotes, showSeeAllToggle]);

  // Persist showRadiusCircle to sessionStorage only when user explicitly toggles
  useEffect(() => {
    if (hasUserToggledCircle) {
      sessionStorage.setItem(
        "traces_show_radius_circle_session",
        JSON.stringify(showRadiusCircle),
      );
    }
  }, [showRadiusCircle, hasUserToggledCircle]);

  // Sync with settings when they change (e.g., user saved in Settings page)
  // This ensures home page immediately reflects changes to the default setting
  useEffect(() => {
    if (settings?.showRadiusCircle !== undefined) {
      // Check if sessionStorage was cleared (by SettingsContext after save)
      const sessionOverride = sessionStorage.getItem(
        "traces_show_radius_circle_session",
      );
      if (sessionOverride === null) {
        // Settings were just saved, reset the toggle tracking and sync
        setHasUserToggledCircle(false);
        setShowRadiusCircle(settings.showRadiusCircle);
      }
    }
  }, [settings?.showRadiusCircle]);

  // Periodically refresh location while on Home page.
  // Keeps the map blue dot and radius circle accurate as the user moves.
  // The 15s interval matches the maxAge passed to requestLocation,
  // so if the cached location is still fresh, the call is a no-op.
  // Pauses when the tab is hidden to save battery/GPS on mobile.
  const LOCATION_REFRESH_INTERVAL = 15000; // 15 seconds

  useEffect(() => {
    if (!isVisible) return;

    devLog("Home: Requesting location on mount/visible");
    requestLocation(LOCATION_REFRESH_INTERVAL);

    const interval = setInterval(() => {
      devLog("Home: Periodic location refresh");
      requestLocation(LOCATION_REFRESH_INTERVAL);
    }, LOCATION_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [isVisible]);

  // Handle "fit new nearby notes" request from notifications page (legacy bulk notifications)
  const routerLocation = useRouterLocation();
  const [fitBoundsRequest, setFitBoundsRequest] = useState(null);

  useEffect(() => {
    if (!routerLocation.state?.fitNewNearbyNotes) return;

    // Clear the navigation state so it doesn't re-trigger
    window.history.replaceState({}, "");

    api.getNewNearbyNotes().then((response) => {
      if (response.data.length > 0) {
        const coords = response.data.map((n) => [n.longitude, n.latitude]);
        setFitBoundsRequest(coords);
      }
    }).catch((err) => {
      logError("Failed to fetch new nearby notes:", err);
    });
  }, [routerLocation.state?.fitNewNearbyNotes]);

  const [openNote, setOpenNote] = useState(false);

  const handleNoteMarkerClick = async (note) => {
    // If clicking the same note that's already open, just toggle modal
    if (selectedNote?.id === note.id && openNote) {
      setOpenNote(false);
      setSelectedNote(null);
      return;
    }

    try {
      setOpenNote(true);
      setSelectedNote(note); // Set immediately for instant glow (optimistic)

      // Fetch fresh note data with updated like status through the hook
      const freshNote = await fetchNote(note.id);
      setSelectedNote(freshNote);
    } catch (err) {
      logError("Failed to load note:", err);
      // Fallback to cached note data if fetch fails
      setSelectedNote(note);
    }
  };

  const hanndleNoteModalClose = () => {
    setOpenNote(false);
    setSelectedNote(null);
  };

  const handleNoteDelete = async (noteId) => {
    await deleteNote(noteId);
    // Close modal after deletion
    setOpenNote(false);
    setSelectedNote(null);
  };

  const handleNoteUpdate = async (noteId) => {
    try {
      // Fetch fresh note data (updates notes list via hook)
      const freshNote = await fetchNote(noteId);
      // Also update the selected note so modal shows latest data
      setSelectedNote(freshNote);
    } catch (err) {
      logError("Failed to refresh note after update:", err);
    }
  };

  const handleNoteCreated = async (newNote) => {
    // modal already called API and succeeded
    refetch();
  };

  return (
    <>
      <div className="flex-1 bg-surface flex items-center justify-center text-text-muted text-lg">
        {locationError && (
          <BlockingModal
            error={locationError}
            onRetry={locationError.retryAction}
          />
        )}
        {!locationError && !userLocation && locationLoading && (
          <div className="w-full h-full flex items-center justify-center text-text-muted">
            <div className="flex items-center justify-center gap-3">
              <Spinner size={26} className="text-text-muted/60" />
              {/* <span>Map loading...</span> */}
            </div>
          </div>
        )}
        {userLocation && (
          <>
            <MapboxMap
              notes={notes}
              selectedNote={selectedNote}
              initialViewState={{
                longitude: userLocation.longitude,
                latitude: userLocation.latitude,
                bounds: computeRadiusBounds(
                  userLocation.latitude,
                  userLocation.longitude,
                  radius,
                  unit,
                ),
              }}
              committedRadius={(showSeeAllToggle && seeAllNotes) ? userMaxRange : committedRadius}
              onMarkerClick={handleNoteMarkerClick}
              searchRadius={(showSeeAllToggle && seeAllNotes) ? userMaxRange : radius}
              showRadiusCircle={showRadiusCircle && effectiveRadius <= 5000}
              fitBoundsRequest={fitBoundsRequest}
              settingsPopover={{
                value: radius,
                onChange: setRadius,
                onChangeCommitted: handleRadiusCommitted,
                min: 1,
                max: userMaxRange,
                step: 0.01225,
                unit: unitSuffix,
                showToggle: true,
                toggleLabel: "Show circle",
                toggleValue: showRadiusCircle,
                onToggleChange: handleRadiusCircleToggle,
                showSeeAllToggle,
                seeAllValue: seeAllNotes,
                onSeeAllChange: setSeeAllNotes,
              }}
            />

            {/* Loading indicator for radius changes */}
            {loading && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-surface-elevated rounded-lg shadow-lg px-4 py-2">
                <div className="flex items-center gap-2">
                  <Spinner size={16} className="text-primary" />
                  <span className="text-sm text-text-secondary">
                    Updating notes...
                  </span>
                </div>
              </div>
            )}

            <NoteModal
              open={openNote}
              note={selectedNote}
              loading={fetchingNote}
              onClose={hanndleNoteModalClose}
              onNoteDeleted={handleNoteDelete}
              onNoteUpdated={handleNoteUpdate}
            />
          </>
        )}
      </div>

      {/* Dedicated Create Note Button */}
      <button
        onClick={() => setShowCreateNoteModal(true)}
        className="fixed bottom-28 md:bottom-10 right-2 md:right-10 z-30 h-12 w-12 rounded-full grid place-items-center bg-surface-elevated border border-border shadow-lg hover:bg-surface active:bg-surface-sunken"
        aria-label="Create a new note"
      >
        <PencilIcon className="h-5 w-5 text-primary" />
      </button>
      {showCreateNoteModal && (
        <CreateNoteModal
          onClose={() => {
            setShowCreateNoteModal(false);
            // Resume onboarding if it was paused for note creation
            // (onClose is always called — after onSuccess on success, or alone on cancel)
            if (onboarding.isPaused) {
              onboarding.nextStep();
              onboarding.resumeOnboarding();
            }
          }}
          onSuccess={(note) => handleNoteCreated(note)}
          initialCoordinates={{
            latitude: userLocation?.latitude,
            longitude: userLocation?.longitude,
          }}
          createNote={createNote}
        />
      )}

      {/* Onboarding overlay */}
      {onboarding.showOnboarding && !onboarding.isPaused && !onboarding.loading && (
        <OnboardingOverlay
          currentStep={onboarding.currentStep}
          currentStepIndex={onboarding.currentStepIndex}
          totalSteps={onboarding.totalSteps}
          onNext={onboarding.nextStep}
          onSkip={onboarding.skipStep}
          onBack={onboarding.prevStep}
          onGoToStep={onboarding.goToStep}
          onComplete={onboarding.completeOnboarding}
          onCreateNote={() => {
            onboarding.pauseOnboarding();
            setShowCreateNoteModal(true);
          }}
        />
      )}
    </>
  );
}
